import { api } from '@/config/api';

export interface BusinessReport {
  generatedAt: string;
  range: { from: string; to: string; label: string };
  company: { name: string; industry?: string; tagline?: string };
  leads: {
    total: number;
    bySource: Array<{ source: string; count: number }>;
    byStatus: Record<string, number>;
    conversionToQualified: number;
    conversionToWon: number;
  };
  sales: {
    byCurrency: Array<{ currency: string; dealsWon: number; revenueWon: number; averageWonDeal: number; pipelineValue: number; openDeals: number }>;
    openDeals: number; unresolvedOutcomes: number;
    dealsCreated: number;
    dealsWon: number;
    dealsLost: number;
    revenueWonUsd: number;
    pipelineValueUsd: number;
    avgDealSizeUsd: number;
  };
  campaigns: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    deliveryRate: number;
    readRate: number;
  };
  advertising: Array<{ platform: string; currency: string; spend: number; impressions: number; clicks: number; snapshotRows: number; coveredDays: number; lastSyncedAt?: string; capturedLeads: number }>;
  ai: {
    succeeded: number; failed: number; pending: number; chargedCredits: number; unpricedRequests: number;
    totalReplies: number;
    qualifiedByAi: number;
    totalCostUsd: number | null;
    totalTokens: number;
  };
  documents: {
    quotations: number;
    invoices: number;
  };
}

export interface WinLossSummary {
  range: { from: string; to: string };
  totals: { won: number; lost: number; open: number; winRatePct: number; avgDaysToClose: number | null };
  wonValueByCurrency: Record<string, number>;
  lostReasons: Array<{ reason: string; count: number; value: number; currency: string }>;
  byOwner: Array<{ ownerUserUid: string; won: number; lost: number; winRatePct: number; wonValueByCurrency: Record<string, number> }>;
}

const BASE = '/slighter_crm/reports';

export const reportsService = {
  business: async (filter: { from?: string; to?: string } = {}): Promise<BusinessReport> =>
    (await api.get(`${BASE}/business`, { params: filter })).data,

  winLoss: async (filter: { from?: string; to?: string } = {}): Promise<WinLossSummary> =>
    (await api.get(`${BASE}/win-loss`, { params: filter })).data,

  /** Browser-tab URL — `Download PDF` button on the rendered HTML triggers print-to-PDF. */
  businessHtml: async (filter: { from?: string; to?: string } = {}): Promise<Blob> => (await api.get(`${BASE}/business.html`, { params: filter, responseType: 'blob' })).data,
  businessHtmlUrl: (filter: { from?: string; to?: string } = {}): string => {
    const base = (process.env.REACT_APP_API_BASE ?? '').replace(/\/$/, '');
    const params = new URLSearchParams();
    if (filter.from) params.set('from', filter.from);
    if (filter.to) params.set('to', filter.to);
    const qs = params.toString();
    return `${base}${BASE}/business.html${qs ? `?${qs}` : ''}`;
  },

  // ── Email digest subscription ──────────────────────────────────────────
  getDigest: async (): Promise<DigestSubscription | null> =>
    (await api.get(`${BASE}/digest/me`)).data,
  subscribeDigest: async (frequency: 'daily' | 'weekly' | 'monthly' = 'weekly'): Promise<DigestSubscription> =>
    (await api.put(`${BASE}/digest/me`, { frequency })).data,
  unsubscribeDigest: async (): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/digest/me/unsubscribe`)).data,
  sendDigestNow: async (): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/digest/me/send-now`)).data,
};

export interface DigestSubscription {
  subscriptionUid: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  enabled: boolean;
  nextSendAt: string;
  lastSentAt?: string;
  totalSent: number;
  lastError?: string;
}
